import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaSave, FaPlus, FaTrash, FaExclamationTriangle, FaUser, FaUserPlus, FaUserMinus, FaEnvelope, FaQuestionCircle, FaTimes, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AuthContext from '../../context/AuthContext';

// CSS for spinner animation
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const OrganizationDetails = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: '',
    website: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    contactName: '',
    contactEmail: '',
    contactPhone: ''
  });
  
  // User management state
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'user'
  });
  const [selectedExistingUser, setSelectedExistingUser] = useState('');
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showExistingUserDropdown, setShowExistingUserDropdown] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  // Get user's assigned organization IDs
  const getUserOrganizationIds = () => {
    if (!user) return [];
    
    const orgIds = [];
    
    // Add organizations from array if it exists
    if (Array.isArray(user.organizations) && user.organizations.length > 0) {
      user.organizations.forEach(org => {
        if (typeof org === 'string') {
          orgIds.push(org);
        } else if (org && org._id) {
          orgIds.push(org._id);
        }
      });
    }
    
    // Add primary organization if it exists and isn't already in the list
    if (user.organization) {
      const primaryOrgId = typeof user.organization === 'string' ? 
        user.organization : 
        (user.organization._id ? user.organization._id : null);
      
      if (primaryOrgId && !orgIds.includes(primaryOrgId)) {
        orgIds.push(primaryOrgId);
      }
    }
    
    console.log('User organization IDs from frontend:', orgIds);
    return orgIds;
  };
  
  useEffect(() => {
    // Clear organization-related session storage on component mount
    sessionStorage.removeItem('selectedOrganizationId');
    sessionStorage.removeItem('currentSelectedOrganization');
    
    // Force reload user data to ensure we have the latest organization assignments
    if (user) {
      const loadUserData = async () => {
        try {
          // Force reload user data from server
          await axios.get('/api/auth/user');
          fetchOrganizations();
        } catch (err) {
          console.error('Error refreshing user data:', err);
          fetchOrganizations();
        }
      };
      
      loadUserData();
    } else {
      fetchOrganizations();
    }
  }, [user]);
  
  // Fetch users for the selected organization
  useEffect(() => {
    if (selectedOrganization) {
      // Ensure we're using the organization ID, not the full object
      const orgId = typeof selectedOrganization === 'object' ? selectedOrganization._id : selectedOrganization;
      if (orgId) {
        fetchOrganizationUsers(orgId);
      }
    }
  }, [selectedOrganization]);

  const fetchOrganizations = async () => {
    try {
      console.log('Fetching organizations for current user only');
      setLoading(true);
      setError('');
      
      // First get the current user data to ensure we have the latest organization assignments
      const userResponse = await axios.get('/api/auth/user');
      console.log('Current user data:', userResponse.data);
      
      if (!userResponse.data) {
        console.log('No user data found');
        setOrganizations([]);
        setLoading(false);
        setError('Unable to retrieve user data. Please try logging in again.');
        return;
      }
      
      // Extract user's organization IDs directly from the user data
      const currentUser = userResponse.data;
      const userOrgIds = [];
      
      // Add organizations from array if it exists
      if (Array.isArray(currentUser.organizations) && currentUser.organizations.length > 0) {
        currentUser.organizations.forEach(org => {
          if (typeof org === 'string') {
            userOrgIds.push(org);
          } else if (org && org._id) {
            userOrgIds.push(org._id);
          }
        });
      }
      
      // Add primary organization if it exists and isn't already in the list
      if (currentUser.organization) {
        const primaryOrgId = typeof currentUser.organization === 'string' ? 
          currentUser.organization : 
          (currentUser.organization._id ? currentUser.organization._id : null);
        
        if (primaryOrgId && !userOrgIds.includes(primaryOrgId)) {
          userOrgIds.push(primaryOrgId);
        }
      }
      
      console.log('User organization IDs from user data:', userOrgIds);
      
      if (userOrgIds.length === 0) {
        console.log('User has no organizations assigned');
        setOrganizations([]);
        setLoading(false);
        setError('You do not have any organizations assigned to your account.');
        return;
      }
      
      // Now fetch only the organizations the user is assigned to
      const res = await axios.get('/api/organizations/user');
      console.log('Organizations returned from API:', res.data);
      
      if (!res.data || res.data.length === 0) {
        console.log('No organizations found for user');
        setOrganizations([]);
        setLoading(false);
        setError('You do not have any organizations assigned to your account.');
        return;
      }
      
      // Set the organizations
      setOrganizations(res.data);
      
      // Check if there's a saved organization ID in session storage
      const savedOrgId = sessionStorage.getItem('selectedOrganizationId');
      console.log('Saved organization ID from session storage:', savedOrgId);
      
      // If there's a saved org ID and it exists in the fetched orgs, select it
      if (savedOrgId && res.data.some(org => org._id === savedOrgId)) {
        console.log('Using saved organization ID');
        setSelectedOrganization(savedOrgId);
        const selectedOrg = res.data.find(org => org._id === savedOrgId);
        setFormData({
          name: selectedOrg.name || '',
          industry: selectedOrg.industry || '',
          size: selectedOrg.size || '',
          website: selectedOrg.website || '',
          phone: selectedOrg.phone || '',
          streetAddress: selectedOrg.address?.street || '',
          city: selectedOrg.address?.city || '',
          state: selectedOrg.address?.state || '',
          zipCode: selectedOrg.address?.zipCode || '',
          country: selectedOrg.address?.country || '',
          contactName: selectedOrg.primaryContact?.name || '',
          contactEmail: selectedOrg.primaryContact?.email || '',
          contactPhone: selectedOrg.primaryContact?.phone || ''
        });
      } else if (res.data.length > 0) {
        // Otherwise select the first organization
        console.log('Selecting first organization:', res.data[0].name);
        setSelectedOrganization(res.data[0]._id);
        setFormData({
          name: res.data[0].name || '',
          industry: res.data[0].industry || '',
          size: res.data[0].size || '',
          website: res.data[0].website || '',
          phone: res.data[0].phone || '',
          streetAddress: res.data[0].address?.street || '',
          city: res.data[0].address?.city || '',
          state: res.data[0].address?.state || '',
          zipCode: res.data[0].address?.zipCode || '',
          country: res.data[0].address?.country || '',
          contactName: res.data[0].primaryContact?.name || '',
          contactEmail: res.data[0].primaryContact?.email || '',
          contactPhone: res.data[0].primaryContact?.phone || ''
        });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations. Please refresh the page.');
      setLoading(false);
    }
  };

  const updateFormWithOrganization = (org) => {
    setFormData({
      name: org.name || '',
      industry: org.industry || '',
      size: org.size || '',
      website: org.website || '',
      phone: org.phone || '',
      streetAddress: org.address?.street || '',
      city: org.address?.city || '',
      state: org.address?.state || '',
      zipCode: org.address?.zipCode || '',
      country: org.address?.country || '',
      contactName: org.primaryContact?.name || '',
      contactEmail: org.primaryContact?.email || '',
      contactPhone: org.primaryContact?.phone || ''
    });
  };

  const handleSelectOrganization = (org) => {
    // Always store just the ID in selectedOrganization
    setSelectedOrganization(org._id);
    updateFormWithOrganization(org);
    
    // Store the selected organization ID in sessionStorage
    sessionStorage.setItem('selectedOrganizationId', org._id);
    // For backward compatibility
    sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(org));
    
    // Dispatch a custom event to notify other components about the change
    const organizationChangedEvent = new Event('organizationChanged');
    window.dispatchEvent(organizationChangedEvent);
    
    // Clear any messages
    setError('');
    setSuccess('');
    setConfirmDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedOrganization) {
      setError('No organization selected');
      return;
    }
    
    // Ensure we're using the organization ID, not the full object
    const orgId = typeof selectedOrganization === 'object' ? selectedOrganization._id : selectedOrganization;
    
    try {
      setLoading(true);
      setSaveStatus('saving');
      setError('');
      setSuccess('');
      
      // Format the data for the API
      const updatedOrg = {
        name: formData.name,
        industry: formData.industry,
        size: formData.size,
        website: formData.website,
        phone: formData.phone,
        address: {
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        primaryContact: {
          name: formData.contactName,
          email: formData.contactEmail,
          phone: formData.contactPhone
        }
      };
      
      // Update the organization
      const res = await axios.put(`/api/organizations/${orgId}`, updatedOrg);
      
      // Update the organizations list
      const updatedOrgs = organizations.map(org => {
        if (org._id === orgId) {
          return { ...org, ...res.data };
        }
        return org;
      });
      
      setOrganizations(updatedOrgs);
      setSuccess('Organization updated successfully');
      toast.success('Organization updated successfully');
      setSaveStatus('saved');
      
      // Reset save status after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (err) {
      console.error('Error updating organization:', err.message || err);
      const errorMessage = err.response?.data?.msg || err.response?.data?.message || 'Failed to update organization';
      setError(errorMessage);
      toast.error(errorMessage);
      setSaveStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrganization = () => {
    // Reset form for new organization
    setFormData({
      name: '',
      industry: '',
      size: '',
      website: '',
      phone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      contactName: user?.name || '',
      contactEmail: user?.email || '',
      contactPhone: ''
    });
    
    setSelectedOrganization(null);
    setError('');
    setSuccess('');
    setConfirmDelete(null);
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // Format the data for the API
      const newOrg = {
        name: formData.name,
        industry: formData.industry,
        size: formData.size,
        website: formData.website,
        phone: formData.phone,
        address: {
          street: formData.streetAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        primaryContact: {
          name: formData.contactName,
          email: formData.contactEmail,
          phone: formData.contactPhone
        }
      };
      
      // Create the organization
      const res = await axios.post('/api/organizations', newOrg);
      
      // Update the organizations list
      setOrganizations([...organizations, res.data]);
      // Always store just the ID in selectedOrganization
      setSelectedOrganization(res.data._id);
      
      // Update sessionStorage with the ID
      sessionStorage.setItem('selectedOrganizationId', res.data._id);
      // For backward compatibility
      sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(res.data));
      
      // Dispatch event to update other components
      const organizationChangedEvent = new Event('organizationChanged');
      window.dispatchEvent(organizationChangedEvent);
      
      setSuccess('Organization created successfully');
      setLoading(false);
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization. Please try again.');
      setLoading(false);
    }
  };

  // Fetch users for an organization
  const fetchOrganizationUsers = async (organizationId) => {
    try {
      setUserLoading(true);
      setUserError('');
      
      const res = await axios.get(`/api/organizations/${organizationId}/users`);
      let usersList = res.data || [];
      
      // Check if the current logged-in user is in the list
      const currentUserInList = usersList.some(u => u._id === user.id);
      
      // If the current user is not in the list but should be associated with this organization,
      // add them to the list to ensure they're displayed in the UI
      if (!currentUserInList && user && 
          ((user.organization && user.organization === organizationId) || 
           user.isAdmin || user.isMultiOrg)) {
        // Add the current user to the list
        usersList.push({
          _id: user.id,
          name: user.name,
          email: user.email,
          isCurrentUser: true // Flag to identify this is the current user
        });
      }
      
      setUsers(usersList);
      
      // Also fetch all users in the system for the dropdown
      await fetchAllUsers();
      
      setUserLoading(false);
    } catch (err) {
      console.error('Error fetching organization users:', err);
      setUserError('Failed to load users. Please try again later.');
      setUsers([]);
      setUserLoading(false);
    }
  };
  
  // Fetch all users in the system
  const fetchAllUsers = async () => {
    try {
      // We'll use the admin endpoint if the user is an admin, otherwise we'll use a regular endpoint
      let endpoint = user && user.isAdmin ? '/api/admin/users' : '/api/users/all';
      
      const res = await axios.get(endpoint);
      
      // Filter out users that are already in this organization
      const filteredUsers = res.data.filter(u => {
        // Check if this user is already in the organization
        return !users.some(orgUser => orgUser._id === u._id);
      });
      
      setAllUsers(filteredUsers);
    } catch (err) {
      console.error('Error fetching all users:', err);
      // Don't set an error message here, as this is a secondary feature
      setAllUsers([]);
    }
  };
  
  // Handle input change for new user form
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Toggle add user form
  const toggleAddUserForm = () => {
    setShowAddUserForm(!showAddUserForm);
    setUserError('');
    // Reset form if closing
    if (showAddUserForm) {
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'user'
      });
    }
  };
  
  // Add a user to the organization
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!selectedOrganization) {
      setUserError('No organization selected');
      return;
    }
    
    // Validate form
    if (!newUser.name || !newUser.email) {
      setUserError('Name and email are required');
      return;
    }
    
    // Check if organization already has 5 users (including the current user)
    if (users.length >= 4) {
      setUserError('Maximum of 4 additional users allowed per organization');
      return;
    }
    
    try {
      setUserLoading(true);
      setUserError('');
      
      console.log('Creating user for organization:', selectedOrganization);
      
      // Create the new user
      await axios.post(`/api/organizations/${selectedOrganization}/users`, newUser);
      
      // Refresh the user list
      await fetchOrganizationUsers(selectedOrganization);
      
      // Reset the form and hide it
      setNewUser({
        name: '',
        email: '',
        role: 'user'
      });
      setShowAddUserForm(false);
      
      setSuccess('User created successfully');
      setUserLoading(false);
    } catch (err) {
      console.error('Error creating user:', err);
      setUserError(err.response?.data?.msg || 'Failed to create user. Please try again.');
      setUserLoading(false);
    }
  };

  // Assign an existing user to the organization
  const handleAssignExistingUser = async () => {
    try {
      if (!selectedOrganization) {
        toast.error('Please select an organization first');
        return;
      }
      
      if (!selectedExistingUser) {
        toast.error('Please select a user to assign');
        return;
      }
      
      setUserLoading(true);
      setUserError('');
      
      // Find the selected user from our list
      const userToAssign = allUsers.find(u => u._id === selectedExistingUser);
      
      if (!userToAssign) {
        toast.error('Selected user not found');
        setUserLoading(false);
        return;
      }
      
      // Ensure we're using the organization ID, not the full object
      const orgId = typeof selectedOrganization === 'object' ? selectedOrganization._id : selectedOrganization;
      
      if (!orgId) {
        toast.error('Invalid organization ID');
        setUserLoading(false);
        return;
      }
      
      // Call API to assign the user to this organization
      const res = await axios.put(`/api/organizations/${orgId}/assign-user/${selectedExistingUser}`);
      
      // Update the users list
      setUsers([...users, userToAssign]);
      
      // Remove the assigned user from the allUsers list
      setAllUsers(allUsers.filter(u => u._id !== selectedExistingUser));
      
      // Reset selection
      setSelectedExistingUser('');
      
      // Hide the dropdown
      setShowExistingUserDropdown(false);
      
      toast.success('User assigned successfully');
      setUserLoading(false);
    } catch (err) {
      console.error('Error assigning user:', err);
      
      const errorMsg = err.response?.data?.msg || 'Failed to assign user. Please try again.';
      setUserError(errorMsg);
      toast.error(errorMsg);
      
      setUserLoading(false);
    }
  };
  
  // Remove a user from the organization
  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) {
      return;
    }
    
    if (!selectedOrganization) {
      setUserError('No organization selected');
      toast.error('No organization selected');
      return;
    }
    
    try {
      setUserLoading(true);
      setUserError('');
      
      // Check if user is trying to remove themselves
      if (userId === user.id) {
        const confirmSelf = window.confirm(
          'You are about to remove yourself from this organization. You will no longer have access to it. Are you sure?'
        );
        if (!confirmSelf) {
          setUserLoading(false);
          return;
        }
      }
      
      // selectedOrganization is just the ID, not the full object
      console.log('Removing user from organization:', selectedOrganization);
      
      // Remove the user from the organization
      const response = await axios.delete(`/api/organizations/${selectedOrganization}/users/${userId}`);
      
      // Refresh the user list
      await fetchOrganizationUsers(selectedOrganization);
      
      toast.success('User removed successfully');
      setSuccess('User removed successfully');
      setUserLoading(false);
      
      // If user removed themselves, redirect to dashboard
      if (userId === user.id) {
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Error removing user:', err);
      
      // Handle specific error messages from the server
      if (err.response && err.response.data && err.response.data.msg) {
        setUserError(err.response.data.msg);
        toast.error(err.response.data.msg);
      } else {
        setUserError('Failed to remove user. Please try again.');
        toast.error('Failed to remove user. Please try again.');
      }
      
      setUserLoading(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!selectedOrganization) {
      setError('No organization selected');
      return;
    }
    
    // Only admins can delete organizations
    if (!user.isAdmin) {
      setError('Only administrators can delete organizations');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      // selectedOrganization is just the ID, not the full object
      console.log('Deleting organization:', selectedOrganization);
      
      // Delete the organization
      await axios.delete(`/api/organizations/${selectedOrganization}`);
      
      // Update the local state
      const updatedOrganizations = organizations.filter(org => org._id !== selectedOrganization);
      setOrganizations(updatedOrganizations);
      
      // Select another organization if available
      if (updatedOrganizations.length > 0) {
        setSelectedOrganization(updatedOrganizations[0]);
        updateFormWithOrganization(updatedOrganizations[0]);
        
        // Update sessionStorage
        sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(updatedOrganizations[0]));
        
        // Dispatch event to update other components
        const organizationChangedEvent = new Event('organizationChanged');
        window.dispatchEvent(organizationChangedEvent);
      } else {
        setSelectedOrganization(null);
        handleAddOrganization();
      }
      
      setSuccess('Organization deleted successfully');
      setConfirmDelete(null);
      setLoading(false);
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError('Failed to delete organization. Please try again.');
      setLoading(false);
      setConfirmDelete(null);
    }
  };

  // Styles
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      color: '#243444',
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    addButton: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    },
    orgSelector: {
      display: 'flex',
      gap: '15px',
      marginBottom: '30px',
      flexWrap: 'wrap'
    },
    orgCard: {
      padding: '15px',
      borderRadius: '8px',
      border: '1px solid #eee',
      width: '200px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    activeOrgCard: {
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: '1px solid #243444'
    },
    orgName: {
      fontWeight: 'bold',
      fontSize: '16px',
      marginBottom: '5px'
    },
    orgIndustry: {
      fontSize: '14px',
      color: '#4C5454'
    },
    formContainer: {
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    formSection: {
      marginBottom: '20px'
    },
    sectionTitle: {
      color: '#243444',
      fontSize: '18px',
      marginBottom: '15px',
      paddingBottom: '5px',
      borderBottom: '2px solid #D2BA7B'
    },
    formRow: {
      display: 'flex',
      gap: '20px',
      marginBottom: '15px',
      flexWrap: 'wrap'
    },
    formGroup: {
      flex: '1 1 300px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      color: '#243444',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '14px'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px'
    },
    saveButton: {
      backgroundColor: '#243444',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    },
    deleteButton: {
      backgroundColor: '#FFFAFA',
      color: '#7C1227',
      border: '1px solid #7C1227',
      borderRadius: '4px',
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    },
    message: {
      padding: '10px 15px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    error: {
      backgroundColor: '#ffebee',
      color: '#7C1227'
    },
    success: {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32'
    },
    confirmDelete: {
      backgroundColor: '#ffebee',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    confirmButtons: {
      display: 'flex',
      gap: '10px',
      marginTop: '10px'
    },
    confirmYes: {
      backgroundColor: '#7C1227',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      cursor: 'pointer'
    },
    confirmNo: {
      backgroundColor: '#4C5454',
      color: '#FFFAFA',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      cursor: 'pointer'
    },
    loading: {
      textAlign: 'center',
      padding: '20px'
    }
  };

  if (loading && organizations.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading organizations...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Add style tag for spinner animation */}
      <style>{spinAnimation}</style>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaBuilding /> Organization Settings
        </h1>
        {/* Only show Add Organization button for multi-org users */}
        {user && user.isMultiOrg && (
          <button 
            style={styles.addButton}
            onClick={handleAddOrganization}
          >
            <FaPlus /> Add Organization
          </button>
        )}
      </div>

      {error && (
        <div style={{...styles.message, ...styles.error}}>
          {error}
        </div>
      )}

      {success && (
        <div style={{...styles.message, ...styles.success}}>
          {success}
        </div>
      )}

      {confirmDelete && (
        <div style={styles.confirmDelete}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <FaExclamationTriangle color="#7C1227" />
            <strong>Are you sure you want to delete {confirmDelete.name}?</strong>
          </div>
          <p>This action cannot be undone. All assessments and reports associated with this organization will be inaccessible.</p>
          <div style={styles.confirmButtons}>
            <button 
              style={styles.confirmYes}
              onClick={handleDeleteOrganization}
            >
              Yes, Delete
            </button>
            <button 
              style={styles.confirmNo}
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {organizations.length > 0 && (
        <div style={styles.orgSelector}>
          {organizations.map(org => (
            <div 
              key={org._id}
              style={{
                ...styles.orgCard,
                ...(selectedOrganization === org._id ? styles.activeOrgCard : {})
              }}
              onClick={() => handleSelectOrganization(org)}
            >
              <div style={styles.orgName}>{org.name}</div>
              <div style={{
                ...styles.orgIndustry,
                color: selectedOrganization === org._id ? '#D2BA7B' : '#4C5454'
              }}>
                {org.industry || 'No industry'}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={styles.formContainer}>
        <form onSubmit={selectedOrganization ? handleSubmit : handleCreateOrganization}>
          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Organization Details</h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Organization Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                  style={styles.input}
                >
                  <option value="">Select Industry Type</option>
                  <option value="Aerospace & Defense">Aerospace & Defense</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Automotive">Automotive</option>
                  <option value="Aviation">Aviation</option>
                  <option value="Banking">Banking</option>
                  <option value="Biotechnology">Biotechnology</option>
                  <option value="Chemical">Chemical</option>
                  <option value="College & University">College & University</option>
                  <option value="Construction">Construction</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Consumer Goods">Consumer Goods</option>
                  <option value="Education">Education</option>
                  <option value="Energy">Energy</option>
                  <option value="Entertainment & Media">Entertainment & Media</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Government">Government</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Hospitality">Hospitality</option>
                  <option value="Information Security">Information Security</option>
                  <option value="Information Technology">Information Technology</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Legal">Legal</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Mining">Mining</option>
                  <option value="Non-Profit">Non-Profit</option>
                  <option value="Oil & Gas">Oil & Gas</option>
                  <option value="Pharmaceutical">Pharmaceutical</option>
                  <option value="Professional Services">Professional Services</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Retail & E-Commerce">Retail & E-Commerce</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Transportation & Logistics">Transportation & Logistics</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Company Size</label>
                <select
                  id="companySize"
                  name="size"
                  value={formData.size}
                  onChange={handleInputChange}
                  style={styles.input}
                >
                  <option value="">Select Company Size</option>
                  <option value="Small (1-50)">Small (1-50)</option>
                  <option value="Medium (51-500)">Medium (51-500)</option>
                  <option value="Large (501-5000)">Large (501-5000)</option>
                  <option value="Enterprise (5000+)">Enterprise (5000+)</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Website</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Address</h3>
            <div style={styles.formRow}>
              <div style={{...styles.formGroup, flex: '1 1 100%'}}>
                <label style={styles.label}>Street Address</label>
                <input
                  type="text"
                  name="streetAddress"
                  value={formData.streetAddress}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>State/Province</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Zip/Postal Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>Primary Contact</h3>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="text"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          <div style={styles.formSection}>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <h3 style={styles.sectionTitle}>User Management</h3>
              <div style={{ marginLeft: '10px', position: 'relative' }}>
                <FaQuestionCircle 
                  style={{ color: '#243444', cursor: 'pointer' }} 
                  onClick={() => setShowHelpTooltip(!showHelpTooltip)}
                />
                {showHelpTooltip && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    zIndex: 10,
                    width: '300px',
                    backgroundColor: '#FFFAFA',
                    border: '1px solid #D2BA7B',
                    borderRadius: '4px',
                    padding: '15px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    marginTop: '10px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <strong style={{ color: '#243444' }}>User Creation Help</strong>
                      <FaTimes 
                        style={{ cursor: 'pointer', color: '#4C5454' }} 
                        onClick={() => setShowHelpTooltip(false)}
                      />
                    </div>
                    <p style={{ margin: '0', fontSize: '14px', color: '#4C5454' }}>
                      When you create a new user, they will be notified via email with instructions to log in to Business Resilience Navigator. The email will include their initial password. Please advise them to check their junk/spam folder if they do not see the email in their inbox.
                    </p>
                  </div>
                )}
              </div>
            </div>
            {userError && (
              <div style={{...styles.message, ...styles.error, marginBottom: '15px'}}>
                {userError}
              </div>
            )}
            
            <div style={{marginBottom: '20px'}}>
              <p style={{marginBottom: '10px', color: '#243444'}}>
                <FaUser style={{marginRight: '8px'}} /> 
                Users with access to this organization ({users.length}/5)
              </p>
              
              {userLoading ? (
                <div>Loading users...</div>
              ) : users.length === 0 ? (
                <div>No users found for this organization.</div>
              ) : (
                <ul style={{listStyle: 'none', padding: 0}}>
                  {users.map(orgUser => (
                    <li key={orgUser._id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '8px',
                      backgroundColor: orgUser._id === user.id ? '#f0f5ff' : '#f5f5f5',
                      borderRadius: '4px',
                      border: orgUser._id === user.id ? '1px solid #D2BA7B' : 'none'
                    }}>
                      <div>
                        <div style={{fontWeight: 'bold'}}>
                          {orgUser.name} {orgUser._id === user.id && '(You)'}
                        </div>
                        <div style={{fontSize: '14px', color: '#4C5454'}}>{orgUser.email}</div>
                      </div>
                      {orgUser._id === user.id ? (
                        <button 
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove yourself from this organization? You will no longer have access to it.')) {
                              handleRemoveUser(orgUser._id);
                            }
                          }}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#7C1227',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          disabled={userLoading}
                        >
                          <FaUserMinus /> Leave Organization
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleRemoveUser(orgUser._id)}
                          style={{
                            backgroundColor: 'transparent',
                            color: '#7C1227',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          disabled={userLoading}
                        >
                          <FaUserMinus /> Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              
              {users.length < 5 && (
                <div style={{marginTop: '15px'}}>
                  <h4 style={{marginBottom: '10px', color: '#243444'}}>Add User</h4>
                  
                  {!showAddUserForm && !showExistingUserDropdown ? (
                    <div style={{display: 'flex', gap: '10px', marginBottom: '15px'}}>
                      <button 
                        onClick={toggleAddUserForm}
                        style={{
                          backgroundColor: '#243444',
                          color: '#FFFAFA',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '10px 15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        <FaUserPlus /> Add New User
                      </button>
                      
                      <button 
                        onClick={() => setShowExistingUserDropdown(true)}
                        style={{
                          backgroundColor: '#4C5454',
                          color: '#FFFAFA',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '10px 15px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer'
                        }}
                        disabled={allUsers.length === 0}
                        title={allUsers.length === 0 ? 'No available users to assign' : 'Assign existing user'}
                      >
                        <FaUserPlus /> Assign Existing User
                      </button>
                    </div>
                  ) : showExistingUserDropdown ? (
                    <div style={{border: '1px solid #D2BA7B', borderRadius: '4px', padding: '15px', marginBottom: '15px'}}>
                      <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#243444'}}>
                          Select Existing User
                        </label>
                        <select
                          value={selectedExistingUser}
                          onChange={(e) => setSelectedExistingUser(e.target.value)}
                          style={{
                            ...styles.input,
                            backgroundColor: '#FFFAFA'
                          }}
                          required
                        >
                          <option value="">-- Select a user --</option>
                          {allUsers.map(user => (
                            <option key={user._id} value={user._id}>
                              {user.name} ({user.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                        <button 
                          onClick={() => {
                            setShowExistingUserDropdown(false);
                            setSelectedExistingUser('');
                          }}
                          style={{
                            backgroundColor: '#4C5454',
                            color: '#FFFAFA',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 15px',
                            cursor: 'pointer'
                          }}
                          disabled={userLoading}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={handleAssignExistingUser}
                          style={{
                            backgroundColor: '#243444',
                            color: '#FFFAFA',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                          }}
                          disabled={userLoading || !selectedExistingUser}
                        >
                          <FaUserPlus /> Assign User
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{border: '1px solid #D2BA7B', borderRadius: '4px', padding: '15px', marginBottom: '15px'}}>
                      <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#243444'}}>
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newUser.name}
                          onChange={handleNewUserChange}
                          placeholder="Enter user's full name"
                          style={styles.input}
                          required
                        />
                      </div>
                      
                      <div style={{marginBottom: '15px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#243444'}}>
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={newUser.email}
                          onChange={handleNewUserChange}
                          placeholder="Enter email address"
                          style={styles.input}
                          required
                        />
                      </div>
                      
                      {/* Password field removed - now sent automatically via email */}
                      
                      <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                        <button 
                          onClick={toggleAddUserForm}
                          style={{
                            backgroundColor: '#4C5454',
                            color: '#FFFAFA',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 15px',
                            cursor: 'pointer'
                          }}
                          disabled={userLoading}
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={handleCreateUser}
                          style={{
                            backgroundColor: '#243444',
                            color: '#FFFAFA',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                          }}
                          disabled={userLoading}
                        >
                          <FaUserPlus /> Create User
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={styles.buttonContainer}>
            <button 
              type="submit" 
              style={styles.saveButton}
              disabled={loading || saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? (
                <>
                  <FaSpinner className="fa-spin" style={{ animation: 'spin 1s linear infinite' }} /> Saving...
                </>
              ) : saveStatus === 'saved' ? (
                <>
                  <FaCheckCircle style={{ color: '#D2BA7B' }} /> Saved
                </>
              ) : (
                <>
                  <FaSave /> {selectedOrganization ? 'Save Changes' : 'Create Organization'}
                </>
              )}
            </button>
            
            {selectedOrganization && organizations.length > 1 && (
              <button 
                type="button" 
                style={{
                  ...styles.deleteButton,
                  opacity: user.isAdmin ? 1 : 0.5,
                  cursor: user.isAdmin ? 'pointer' : 'not-allowed'
                }}
                onClick={() => user.isAdmin && setConfirmDelete(selectedOrganization)}
                disabled={loading || !user.isAdmin}
                title={user.isAdmin ? 'Delete Organization' : 'Only administrators can delete organizations'}
              >
                <FaTrash /> Delete Organization
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationDetails;
